import * as React from "react";
import "./disclaimer-screen.css";
import { Icon } from "@iconify/react";

import chrome_icon from "assets/ChromeIcon.png";
import FadeInOut from "components/fade-in-out";
import { Headline, Text } from "components/typography";

const animationDuration = 500;

const AnimatedXIcon = ({
  animationDuration,
}: {
  animationDuration: number;
}) => {
  const circumference = 2 * Math.PI * 14.5; // 2 * π * r
  const [offset, setOffset] = React.useState(circumference); // Start with the entire circle hidden

  React.useEffect(() => {
    const timer = setTimeout(() => {
      setOffset(0);
    }, 100);

    return () => {
      clearTimeout(timer);
    };
  }, []);

  return (
    <svg
      width="30"
      height="30"
      viewBox="0 0 30 30"
      data-cy="disclaimer-close-button"
    >
      {/* Black circle */}
      <circle cx="15" cy="15" r="14.5" fill="black" />

      {/* White X in the center */}
      <line x1="9" y1="9" x2="21" y2="21" stroke="white" strokeWidth="2" />
      <line x1="9" y1="21" x2="21" y2="9" stroke="white" strokeWidth="2" />

      {/* White border circle */}
      <circle
        cx="15"
        cy="15"
        r="14.5"
        fill="none"
        stroke="white"
        strokeWidth="2"
        strokeDasharray={circumference}
        strokeDashoffset={offset}
        transform="rotate(-90, 15, 15)"
        style={{
          transition: `stroke-dashoffset ${animationDuration}ms ease-out`,
        }}
      />
    </svg>
  );
};

export const DisclaimerScreen = ({
  show,
  setShow,
}: {
  show: boolean;
  setShow: React.Dispatch<React.SetStateAction<boolean>>;
}) => {
  const [showClose, setShowClose] = React.useState(false);

  React.useEffect(() => {
    if (show) setShowClose(true);
  }, [show]);

  const isChrome = React.useMemo(
    () => navigator.userAgent.indexOf("Chrome") > -1,
    [],
  );
  return (
    <FadeInOut
      show={show}
      animationDuration={animationDuration}
      additionalClasses={show ? "splash-screen-fade" : ""}
    >
      <div className="splash-screen-disclaimer">
        <div className="disclaimer-container">
          <div
            className="disclaimer-close-button"
            onClick={() => setShow(false)}
            role="button"
          >
            {showClose && <AnimatedXIcon animationDuration={5000} />}
          </div>
          <Headline level={3} className="text-left mb-2">
            Disclaimer:
          </Headline>
          <Text level={2} className="text-left">
            The results are based on global models. They can inform local
            decisions but caution is urged.
          </Text>
          {!isChrome && (
            <>
              <br />
              <div className="browser-disclaimer-container-parent">
                <div className="browser-disclaimer-container">
                  <div className="browser-disclaimer-text">
                    <Text level={3}>We recommend Chrome.</Text>
                  </div>
                  <div className="browser-icon">
                    <img src={chrome_icon} alt="" />
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </FadeInOut>
  );
};

export const NavigationControls = ({
  show,
  isTouch,
}: {
  show: boolean;
  isTouch: boolean;
}) => {
  return !isTouch ? (
    <FadeInOut
      show={show}
      animationDuration={animationDuration}
      additionalClasses={show ? "navigation-controls-fade" : ""}
    >
      <div className="navigation-controls-parent">
        <div className="navigation-controls">
          <div className="navigation-icon">
            <Headline level={5} className="mb-1">
              Pan
            </Headline>
            <Icon
              icon="mdi:mouse-left-click-outline"
              color="white"
              width={42}
            />
          </div>
          <div className="navigation-icon">
            <Headline level={5} className="mb-1">
              Zoom
            </Headline>
            <Icon icon="iconoir:mouse-scroll-wheel" color="white" width={42} />
          </div>
          <div className="navigation-icon">
            <Headline level={5} className="mb-1">
              Tilt
            </Headline>
            <Icon
              icon="mdi:mouse-right-click-outline"
              color="white"
              width={42}
            />
          </div>
        </div>
      </div>
    </FadeInOut>
  ) : null;
};
